/*
 *  Copyright © Microsoft Corporation. All rights reserved.
 */

import { ValidationState } from '@fluentui/react/lib/Pickers';
import { UploadStatus } from '@msteams/services-io-browser-file-upload';
import { Constants } from '../../../../common/Constants';
import { DocusignConstants } from '../../../../common/DocusignConstants';
import { DocusignFileAttachment } from '../../../../models/fileService/DocusignFileAttachment';
import { getAppHost } from '../../../../utilities/TeamsUtils';
import { Recipient } from '../../RecipientList';
import { EsignForm, useCreateEsignEnvelopeForm } from './useCreateEsignEnvelopeForm';
import { InputState } from './useInputState';

const titleValidation = (value: string): boolean => {
    return value.trim().length > 0;
};

const filesValidation = (value: DocusignFileAttachment[]): boolean => {
    return (
        value &&
        value.length > 0 &&
        value.filter(
            (file: DocusignFileAttachment): boolean =>
                file.status !== UploadStatus.COMPLETE || (!file.documentBase64 && !file.documentId)
        ).length === 0
    );
};

const recipientsValidation = (value: Recipient[]): boolean => {
    return (
        value &&
        value.length > 0 &&
        value.every(
            (recipient: Recipient): boolean =>
                recipient.persona &&
                recipient.persona.ValidationState !== ValidationState.invalid &&
                recipient.persona.ValidationState !== ValidationState.warning &&
                !!recipient.persona.email &&
                !!recipient.name
        )
    );
};

export interface DocusignEnvelopeInitialValues {
    title: string;
    setTitle: (value: string) => void;
    details: string;
    setDetails: (value: string) => void;
}

export interface UseCreateDocusignEnvelopeForm extends EsignForm {
    filesInput: InputState<DocusignFileAttachment[]>;
}

export const useCreateDocusignEnvelopeForm = (
    initialValues: DocusignEnvelopeInitialValues
): UseCreateDocusignEnvelopeForm => {
    const host = getAppHost();
    const loc = host.getLocale();

    return useCreateEsignEnvelopeForm({
        titleInputProps: {
            initialValue: initialValues?.title || '',
            setValue: initialValues.setTitle,
            validation: titleValidation,
            maxLength: DocusignConstants.maxTitleCharacterLimit,
        },
        detailsInputProps: {
            initialValue: initialValues.details || '',
            setValue: initialValues.setDetails,
            maxLength: DocusignConstants.maxCreateDetailsCharacterLimit,
        },
        filesInputProps: {
            initialValue: [],
            validation: filesValidation,
        },
        recipientsInputProps: {
            initialValue: [],
            validation: recipientsValidation,
            maxLength: Constants.MAX_NUMBER_OF_RECIPIENTS,
        },
        completeInOrderInputProps: {
            initialValue: false,
        },
        availableRoles: [
            loc.strings.docusign_role_signer,
            loc.strings.docusign_role_cc,
            loc.strings.docusign_role_viewer,
        ],
    });
};
